// Roles and permissions (plan §5). One table used by the Worker, the site Durable Object and the
// UI, so a button is never shown for an action the server would refuse.

export const ROLES = ["platform_operator", "org_admin", "site_admin", "door_staff", "teacher", "auditor", "guardian"] as const;
export type CoreRole = (typeof ROLES)[number];
/** Modules add roles and permissions namespaced by their id ("transport:driver", "radio:use"). */
export type ModuleName = `${string}:${string}`;
export type Role = CoreRole | ModuleName;

export const PERMISSIONS = [
  "site.configure", "site.billing", "staff.manage", "device.enrol", "device.revoke",
  "roster.read", "roster.write", "restriction.write", "restriction.read",
  "handover.capture", "handover.override", "presence.write",
  "events.read_all", "events.read_class", "events.read_own_children",
  "evidence.export", "reports.read", "retention.manage", "audit.read",
  "delegation.write", "integrations.manage",
] as const;
export type CorePermission = (typeof PERMISSIONS)[number];
export type Permission = CorePermission | ModuleName;

const ALL = new Set<CorePermission>(PERMISSIONS);
const minus = (...p: CorePermission[]) => new Set<Permission>([...ALL].filter((x) => !p.includes(x)));

const CORE_ROLE_PERMISSIONS: Record<CoreRole, Set<Permission>> = {
  platform_operator: new Set<Permission>(["audit.read", "reports.read"]), // support views only; no child data by default
  org_admin: minus("events.read_own_children", "delegation.write"),
  site_admin: minus("site.billing", "events.read_own_children", "delegation.write", "integrations.manage"),
  door_staff: new Set<Permission>(["roster.read", "restriction.read", "handover.capture", "presence.write", "events.read_all"]),
  // Teachers see their classes only and cannot export or print (plan §5).
  teacher: new Set<Permission>(["roster.read", "restriction.read", "handover.capture", "presence.write", "events.read_class"]),
  auditor: new Set<Permission>(["events.read_all", "reports.read", "roster.read", "audit.read"]),
  guardian: new Set<Permission>(["events.read_own_children", "delegation.write"]),
};

/** Every role's permissions: the core table, plus what installed modules added (registerModuleAccess). */
export const ROLE_PERMISSIONS = new Map<Role, Set<Permission>>(Object.entries(CORE_ROLE_PERMISSIONS) as [Role, Set<Permission>][]);

export const isRole = (x: unknown): x is Role => typeof x === "string" && ROLE_PERMISSIONS.has(x as Role);
export const permissionsOf = (role: Role): ReadonlySet<Permission> => ROLE_PERMISSIONS.get(role) ?? new Set();

/**
 * A module's roles (with their permissions) and the module permissions it grants to existing
 * roles, e.g. { grants: { "radio:use": ["teacher", ...] } }. Called once per runtime at startup
 * by installModules; installing twice is harmless.
 */
export function registerModuleAccess(m: { roles?: { id: ModuleName; permissions: Permission[] }[]; grants?: Record<ModuleName, Role[]> }) {
  for (const r of m.roles ?? []) ROLE_PERMISSIONS.set(r.id, new Set([...(ROLE_PERMISSIONS.get(r.id) ?? []), ...r.permissions]));
  for (const [perm, roles] of Object.entries(m.grants ?? {}) as [ModuleName, Role[]][]) {
    for (const role of roles) {
      const set = ROLE_PERMISSIONS.get(role);
      if (!set) throw new Error(`grant of ${perm} to unknown role ${role}`);
      set.add(perm);
    }
  }
}

export interface Membership {
  role: Role;
  org_id: string | null;
  site_id: string | null; // null = every site in the organisation
  room_ids?: string[]; // teachers: the classes they are assigned
  expires_at?: number | null; // auditors are time-boxed
}

export function can(memberships: Membership[], perm: Permission, scope: { orgId: string; siteId: string }, now = Date.now()): boolean {
  return memberships.some((m) =>
    permissionsOf(m.role).has(perm)
    && (m.expires_at == null || m.expires_at > now)
    && (m.role === "platform_operator" || m.org_id === scope.orgId)
    && (m.site_id === null || m.site_id === scope.siteId));
}

/** Roles that must present a second factor before acting (plan §4 D7). */
export const REQUIRES_SECOND_FACTOR: ReadonlySet<Role> = new Set(["platform_operator", "org_admin", "site_admin"]);
